import { cn } from "@newxsystems/shared";
import type { BlockSpacing } from "@newxsystems/types";
import type { ReactNode } from "react";
import { getKaajSectionSpacing } from "../../kaaj-section-spacing";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";

export type KaajSectionTone = {
  section: string;
  muted: string;
  card: string;
  chip: string;
  tag: string;
};

export const getKaajSectionTone = (): KaajSectionTone => ({
  section: "text-slate-950",
  muted: "text-slate-600",
  card: "border-slate-200/80 bg-white shadow-[0_24px_80px_rgba(63,92,255,0.10)]",
  chip: "border-blue-200 bg-blue-50 text-blue-700",
  tag: "text-slate-500",
});

const getAlignmentClasses = (alignment?: string) => {
  const value = alignment?.toLowerCase();

  if (!value) {
    return { header: undefined, actions: undefined };
  }

  if (value.includes("center")) {
    return { header: "mx-auto items-center text-center", actions: "justify-center" };
  }

  if (value.includes("right")) {
    return { header: "ml-auto items-end text-right", actions: "justify-end" };
  }

  return { header: "items-start text-left", actions: "justify-start" };
};

export interface KaajSectionShellProps {
  id?: string;
  parentClass?: string;
  className?: string;
  rootClassName?: string;
  containerClassName?: string;
  headerClassName?: string;
  eyebrowClassName?: string;
  headingClassName?: string;
  subheadingClassName?: string;
  actionsClassName?: string;
  background?: string;
  spacing?: BlockSpacing | null;
  alignment?: string;
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export const KaajSectionShell = ({
  id,
  parentClass,
  className,
  rootClassName,
  containerClassName,
  headerClassName,
  eyebrowClassName,
  headingClassName,
  subheadingClassName,
  actionsClassName,
  spacing,
  alignment,
  eyebrow,
  heading,
  subheading,
  actions,
  children,
}: KaajSectionShellProps) => {
  const tone = getKaajSectionTone();
  const alignmentClasses = getAlignmentClasses(alignment);

  return (
    <section
      className={cn(
        "relative overflow-hidden",
        tone.section,
        getKaajSectionSpacing(spacing),
        parentClass,
        rootClassName,
        className,
      )}
      id={id}
    >
      <div className={cn("kaaj-container relative", containerClassName)}>
        {(eyebrow || heading || subheading) && (
          <div
            className={cn(
              "mb-10 flex max-w-4xl flex-col gap-4 md:mb-14",
              alignmentClasses.header,
              headerClassName,
            )}
          >
            {eyebrow && (
              <p
                className={cn(
                  "text-xs font-semibold uppercase tracking-[0.28em] text-blue-600",
                  eyebrowClassName,
                )}
              >
                {eyebrow}
              </p>
            )}
            {heading && (
              <h2 className={cn("kaaj-section-heading max-w-3xl", headingClassName)}>{heading}</h2>
            )}
            {subheading && (
              <RichTextHtml
                className={cn(
                  "max-w-2xl text-base leading-7 md:text-lg",
                  tone.muted,
                  subheadingClassName,
                )}
                html={subheading}
              />
            )}
          </div>
        )}

        {children}

        {actions && (
          <div
            className={cn("mt-12 flex flex-wrap gap-3", alignmentClasses.actions, actionsClassName)}
          >
            {actions}
          </div>
        )}
      </div>
    </section>
  );
};

KaajSectionShell.displayName = "Layout::KaajSectionShell";
